import {
  CartSummaryComponent,
  CheckoutDeliveryMethod,
} from '../cart/cart-summary/cart-summary.component';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { errorMessageFor, setRequiredOnControls } from './utils/checkout-form-helpers';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';

import {
  cartAuthoritativePoints,
  cartHasSingles,
  cartMerchandiseTotal,
} from '../../shared/utils/cart.utils';
import { AuthModalService } from '../../shared/services/auth-modal.service';
import { AuthService } from '../../shared/services/auth.service';
import { PointsService } from '../../shared/services/points.service';
import {
  EarlyAccessGateRejection,
  mapEarlyAccessGate403,
} from '../../shared/perron-points/early-access-gate';
import {
  PERRON_POINTS_COPY,
  withBalance,
  withMissing,
  withPoints,
} from '../../shared/perron-points/perron-points-copy';
import { ButtonComponent } from '../../shared/ui/button/button.component';
import { PpCoinComponent } from '../../shared/ui/pp-coin/pp-coin.component';
import { CartService } from '../../shared/services/cart.service';
import { DeliveryPreferenceService } from '../../shared/services/delivery-preference.service';
import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { GuestProfile, GuestProfileService } from '../../shared/services/guest-profile.service';
import { InputComponent } from '../../shared/ui/input/input.component';
import { LocationsService } from '../../shared/services/locations.service';
import { PaymentService } from '../../shared/services/payment.service';
import {
  PreorderDeliveryMethod,
  PreorderError,
  PreorderPaymentMethod,
  PreorderRequest,
  Product,
  StockConflictItem,
} from '../../shared/models/cart.model';
import { CartDrawerService } from '../../shared/services/cart-drawer.service';
import { Router } from '@angular/router';
import { BlockingNoticeModalComponent } from './components/blocking-notice-modal/blocking-notice-modal.component';
import { UberFlashNoticeModalComponent } from './components/uber-flash-notice-modal/uber-flash-notice-modal.component';
import { CoverageNoticeDialogComponent } from '../../shared/ui/coverage-notice-dialog/coverage-notice-dialog.component';
import { PsDeliveryDestinationsDialogComponent } from '../../shared/ui/ps-delivery-destinations-dialog/ps-delivery-destinations-dialog.component';
import { PaymentDataModalComponent } from './components/payment-data-modal/payment-data-modal.component';
import { CountdownComponent } from '../../shared/ui/countdown/countdown.component';
import { StockAlertModalService } from '../../shared/services/stock-alert-modal.service';
import { CHECKOUT_COPY, PS_DELIVERY_COPY, withDeposit, withMinSubtotal } from './checkout-copy';
import { CrcPipe } from '../../shared/pipes/crc.pipe';
import { formatInteger } from '../../shared/utils/currency.util';
import {
  isPsDeliveryCovered,
  isPsDeliveryProvince,
  psDeliveryPriceFor,
} from '../../shared/services/ps-delivery-coverage';
import { ToastService } from '../../shared/services/toast.service';
import { OneTimeNoticeService } from '../../shared/services/one-time-notice.service';
import { User } from '../../shared/models/user.model';
import { filter, startWith, take } from 'rxjs';

/** Métodos de pago REALES (el apartado es una modalidad propia, no un método). */
type PaymentMethod = 'sinpe' | 'transfer' | 'online';

/** Porcentaje de abono inicial para apartados. Mismo valor que precompras. */
const LAYAWAY_DEPOSIT_PERCENT = 0.3;

/**
 * Subtotal mínimo de mercancía (tras descuentos, SIN envío) para habilitar el apartado
 * (docs/specs/checkout-apartados-umbral-plan.md, D1/D5). Espejo de `LayawayPolicy::minSubtotal()`
 * en la API (config/checkout.php, env LAYAWAY_MIN_SUBTOTAL) — cambiarlo aquí exige desplegar la
 * web; cambiarlo en la API exige tocar su .env. Ambos deben moverse juntos.
 */
const LAYAWAY_MIN_SUBTOTAL = 20000;

/** Claves de los avisos «de una sola vez» (ver OneTimeNoticeService). */
const PROOF_NOTICE_KEY = 'perron.notice.proof';
const LAYAWAY_NOTICE_KEY = 'perron.notice.layaway';
type ContactPatch = Partial<Record<'name' | 'surnames' | 'email' | 'phone', string>>;
type AddressPatch = Partial<
  Record<'province' | 'canton' | 'district' | 'hood' | 'directions', string>
>;

@Component({
  selector: 'app-checkout-page',
  imports: [
    ReactiveFormsModule,
    CrcPipe,
    ButtonComponent,
    InputComponent,
    PpCoinComponent,
    CartSummaryComponent,
    BlockingNoticeModalComponent,
    UberFlashNoticeModalComponent,
    CoverageNoticeDialogComponent,
    PsDeliveryDestinationsDialogComponent,
    PaymentDataModalComponent,
    CountdownComponent,
  ],
  templateUrl: './checkout-page.component.html',
  styleUrl: './checkout-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class CheckoutPageComponent {
  private readonly fb = inject(FormBuilder);
  private readonly cartService = inject(CartService);
  private readonly authService = inject(AuthService);
  private readonly authModalService = inject(AuthModalService);
  private readonly pointsService = inject(PointsService);
  private readonly locationsService = inject(LocationsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly guestProfileService = inject(GuestProfileService);
  private readonly deliveryPref = inject(DeliveryPreferenceService);
  private readonly toastService = inject(ToastService);
  private readonly paymentService = inject(PaymentService);
  private readonly cartDrawerService = inject(CartDrawerService);
  private readonly document = inject(DOCUMENT);
  private readonly notice = inject(OneTimeNoticeService);
  /** Para resolver perezosamente el StockAlertModalService (agotado) sin acoplar su cadena HTTP. */
  private readonly injector = inject(Injector);

  protected readonly cart = this.cartService.cart;

  /**
   * Líneas de Acceso VIP bloqueadas (R4/R6), derivadas en `CartService`.
   * Inhabilitan «Confirmar pedido»: las puertas de `reserve` y
   * `payment-intent` responderían 403 y no queremos gastarle al cliente un
   * intento para descubrirlo.
   */
  protected readonly hasBlockedLines = this.cartService.hasBlockedLines;
  protected readonly blockedLinesNotice = this.cartService.blockedLinesNotice;
  protected readonly currentUser = this.authService.currentUser;
  protected readonly isAuthenticated = this.authService.isAuthenticated;
  protected readonly provinces = this.locationsService.provinces;
  protected readonly locationsLoadError = this.locationsService.loadError;
  protected readonly submitting = signal(false);

  protected readonly ppCopy = PERRON_POINTS_COPY.checkout;
  protected readonly withPoints = withPoints;
  protected readonly withBalance = withBalance;
  protected readonly withMissing = withMissing;

  /** Fila autoritativa de PP de esta compra (mirror de D13, sin envío — igual que en `cart-summary`). */
  protected readonly authoritativePoints = computed(() => cartAuthoritativePoints(this.cart()));

  /** Proyección de saldo (solo logueado: sin sesión no hay saldo que proyectar). */
  protected readonly pointsProjection = computed<{ a: number; b: number } | null>(() => {
    if (!this.isAuthenticated() || this.pointsService.threshold() === null) return null;
    const a = this.pointsService.balance();
    return { a, b: a + this.authoritativePoints() };
  });

  protected readonly reachesTierWithPurchase = computed(() => {
    const projection = this.pointsProjection();
    const threshold = this.pointsService.threshold();
    if (!projection || threshold === null) return false;
    return projection.a < threshold && threshold <= projection.b;
  });

  protected readonly pointsMissingAfterPurchase = computed<number | null>(() => {
    const projection = this.pointsProjection();
    const threshold = this.pointsService.threshold();
    if (!projection || threshold === null) return null;
    const missing = threshold - projection.b;
    return missing > 0 ? missing : null;
  });

  /** Copy del aviso bloqueante de apartados (consentimiento). */
  protected readonly layawayCopy = CHECKOUT_COPY.layawayNotice;
  /** Copy del aviso bloqueante del comprobante (SINPE/transferencia). */
  protected readonly proofCopy = CHECKOUT_COPY.proofNotice;
  /** Copy del selector del método del abono inicial (30%). */
  protected readonly layawaySelectorCopy = CHECKOUT_COPY.layawayDepositSelector;
  /** Métodos directos (sin apartado): siempre los mismos, sin `{deposit}`. */
  protected readonly paymentOptions = CHECKOUT_COPY.paymentOptions;

  /**
   * Tarjeta para el abono del apartado: habilitada SOLO con la pasarela real activa (release-safety,
   * mismo criterio que onSubmit/D2). Con 'stub' el apartado ofrece solo SINPE/transferencia: el camino
   * online+apartado iría por cartToPreorder y el backend lo rechaza (422), un callejón sin salida.
   */
  protected readonly cardEnabled = this.paymentService.providerId === 'onvo';

  /** Controla el aviso bloqueante de «Apartar con abonos». */
  protected readonly layawayNoticeOpen = signal(false);
  /** True tras aceptar el consentimiento: revela el selector del 30%. */
  protected readonly layawayConsented = signal(false);

  /**
   * Controla el aviso bloqueante del comprobante (SINPE/transferencia). Se abre
   * al seleccionar el método y solo la primera vez (OneTimeNoticeService).
   */
  protected readonly proofNoticeOpen = signal(false);

  /**
   * Controla el aviso NO bloqueante al elegir Uber Flash. A diferencia de
   * layaway/proofNotice, se muestra una sola vez POR SESIÓN DE CHECKOUT (no
   * persiste entre visitas): `uberNoticeShown` es un flag en memoria, no
   * `OneTimeNoticeService`/localStorage.
   */
  protected readonly uberNoticeOpen = signal(false);
  protected readonly uberNoticeCopy = CHECKOUT_COPY.uberNotice;
  private uberNoticeShown = false;

  /**
   * Estado del modal de datos de pago + comprobante (SINPE / transferencia).
   * Con create-on-finalize el preorder NO está creado todavía: se crea al pulsar
   * «Finalizar» con el comprobante adjunto, y solo entonces se navega a éxito.
   */
  protected readonly paymentModalOpen = signal(false);
  protected readonly paymentModalMethod = signal<'sinpe' | 'transfer'>('sinpe');
  protected readonly paymentModalIsLayaway = signal(false);
  /** Importe a pagar ahora: total del pedido, o abono del 30% si es apartado. */
  protected readonly paymentModalAmount = signal(0);
  /** Saldo restante tras el abono inicial (solo apartado). */
  protected readonly paymentModalRemaining = signal(0);
  protected readonly paymentModalSubmitting = signal(false);
  protected readonly paymentModalError = signal<string | null>(null);

  /**
   * HOLD de stock abierto en la puerta (SINPE/transferencia) al «Confirmar pedido»: su token se
   * propaga a cart-to-preorder al finalizar, y `reservationExpiresAt` (backend-autoritativo) alimenta
   * el countdown del modal. Spec backend:
   * perronstore-api/docs/specs/checkout-stock-flotante-pedidos-plan.md.
   */
  protected readonly reservationToken = signal<string | null>(null);
  protected readonly reservationExpiresAt = signal<string | null>(null);
  /** Copy del countdown del hold (lleva `{mm:ss}`; el instante lo da el backend). */
  protected readonly reservationTimerLabel = CHECKOUT_COPY.reservation.timer;

  /** Contexto capturado al abrir el modal (el cart aún no se ha vaciado). */
  private pendingContext: { hasSingles: boolean } | null = null;

  protected readonly checkoutForm = this.fb.group({
    contact: this.fb.group({
      name: ['', Validators.required],
      surnames: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
    }),
    delivery: this.fb.group({
      method: this.fb.control<CheckoutDeliveryMethod>('ps', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      shop_name: this.fb.control<string | null>(null),
      address: this.fb.group({
        province: [''],
        canton: [''],
        district: [''],
        hood: [''],
        directions: [''],
      }),
    }),
    payment: this.fb.group({
      // Método REAL del pago/abono (sinpe|transfer|online). Cuando hay apartado,
      // lo escribe el selector del 30% (limitado a sinpe/transfer).
      payment_method: this.fb.control<PaymentMethod | null>(null, {
        validators: [Validators.required],
      }),
      // Modalidad apartado (flag propio, independiente del método).
      is_layaway: this.fb.control<boolean>(false, { nonNullable: true }),
    }),
  });

  // Signals derivados del form. Usamos toSignal (no un getter) para que los
  // computeds y bindings del template reaccionen a los valueChanges del form.
  protected readonly deliveryMethodValue = toSignal(
    this.checkoutForm.controls.delivery.controls.method.valueChanges.pipe(
      startWith(this.checkoutForm.controls.delivery.controls.method.value),
    ),
    { initialValue: this.checkoutForm.controls.delivery.controls.method.value },
  );

  /**
   * Espejos editables de provincia/cantón. A diferencia de `toSignal`, también pueden
   * sincronizarse después de los autofills silenciosos (`emitEvent: false`) sin disparar los resets
   * de la cascada ni perder cantón/distrito.
   */
  protected readonly provinceValue = signal(
    this.checkoutForm.controls.delivery.controls.address.controls.province.value,
  );
  protected readonly cantonValue = signal(
    this.checkoutForm.controls.delivery.controls.address.controls.canton.value,
  );

  protected readonly shopNameValue = toSignal(
    this.checkoutForm.controls.delivery.controls.shop_name.valueChanges.pipe(
      startWith(this.checkoutForm.controls.delivery.controls.shop_name.value),
    ),
    {
      initialValue: this.checkoutForm.controls.delivery.controls.shop_name.value,
    },
  );

  /**
   * «Mensajero de Perrón Store» (ps_delivery): tarifa server-side por provincia, cobertura limitada a
   * 19 cantones del GAM (ver ps-delivery-coverage). El precio, cobertura y dialog de cambio a Correos
   * se gestionan abajo; el mismo flag existe en el modal de precompra (prepurchase-modal.component).
   */
  protected readonly psDeliveryEnabled = true;

  /** True solo para los métodos que necesitan dirección física. */
  protected readonly requiresAddress = computed(() => {
    const m = this.deliveryMethodValue();
    return m === 'home' || m === 'ps_delivery';
  });

  /** True solo si delivery es por tienda partner — muestra el dropdown. */
  protected readonly requiresPartnerShop = computed(
    () => this.deliveryMethodValue() === 'other_shop',
  );

  /** Lista de partner shops disponibles. Vacío si no hay cart o no aplica. */
  protected readonly partnerShops = computed(
    () => this.cart()?.partner_shops?.filter((s) => s.active) ?? [],
  );

  /**
   * True cuando el cart NO es elegible para envío entre tiendas (contiene
   * productos que no son singles). El backend marca esto con
   * `no_apply_for_intershop: true` y, en ese caso, NO incluye partner_shops
   * en el response. UI: deshabilitar la opción "Otra Tienda" con explicación.
   */
  protected readonly intershopBlocked = computed(
    () => this.cart()?.no_apply_for_intershop === true,
  );

  /** Método de pago real elegido (reactivo al form), para el template. */
  protected readonly paymentMethodValue = toSignal(
    this.checkoutForm.controls.payment.controls.payment_method.valueChanges.pipe(
      startWith(this.checkoutForm.controls.payment.controls.payment_method.value),
    ),
    {
      initialValue: this.checkoutForm.controls.payment.controls.payment_method.value,
    },
  );

  /** Modalidad apartado activa (reactivo al toggle). */
  protected readonly isLayaway = toSignal(
    this.checkoutForm.controls.payment.controls.is_layaway.valueChanges.pipe(
      startWith(this.checkoutForm.controls.payment.controls.is_layaway.value),
    ),
    {
      initialValue: this.checkoutForm.controls.payment.controls.is_layaway.value,
    },
  );

  /**
   * Elegibilidad estimada del apartado (D1/D2, docs/specs/checkout-apartados-umbral-plan.md): el
   * carrito debe alcanzar `LAYAWAY_MIN_SUBTOTAL` en mercancía sin envío. El helper parte del total
   * de productos del cart y descuenta líneas legacy de transporte; la API vuelve a calcular desde
   * sus líneas de mercancía y conserva la decisión autoritativa (Nota de auditoría 2 del spec).
   */
  protected readonly layawayEligible = computed<boolean>(
    () => cartMerchandiseTotal(this.cart()) >= LAYAWAY_MIN_SUBTOTAL,
  );

  /** Meta del toggle de apartado con el abono inicial (30%) ya formateado. */
  protected readonly layawayToggleMeta = computed(() => {
    const deposit = formatInteger(this.layawayDeposit());
    return withDeposit(CHECKOUT_COPY.layawayToggle.meta, deposit);
  });

  /** Pista bajo el toggle deshabilitado (D2): umbral mínimo ya formateado. */
  protected readonly layawayDisabledHint = computed(() => {
    const min = formatInteger(LAYAWAY_MIN_SUBTOTAL);
    return withMinSubtotal(CHECKOUT_COPY.layawayToggle.disabledHint, min);
  });

  /**
   * Hay ≥1 single en el cart → mostramos el aviso de stock compartido con la
   * tienda física. Lee `product_type` de la relación embebida en cada línea.
   */
  protected readonly hasSingles = computed(() => cartHasSingles(this.cart()));

  /**
   * Precio de envío local-derivado del método y, si aplica, del partner
   * shop seleccionado. NO mutamos `cart().delivery` (el cart es readonly
   * y propiedad del CartService). Este computed alimenta el sidebar y se
   * enviará en el payload de cartToPreorder en 6e.
   *
   * Política por método:
   *   - 'ps' / 'uber' → 0.
   *   - 'ps_delivery' → tarifa según la provincia cubierta (el servidor es autoritativo y la re-deriva).
   *   - 'home' → precio fijo de Correos, salvo umbral de envío gratis.
   *   - 'other_shop' → precio de la partner shop seleccionada.
   */
  protected readonly computedDeliveryPrice = computed<number>(() => {
    const method = this.deliveryMethodValue();
    const cart = this.cart();

    // 'ps' / 'uber' → no hay cargo.
    if (method === 'ps' || method === 'uber') return 0;

    // Mensajero propio: tarifa por provincia. La cobertura la garantiza la UI (dialog / opción
    // deshabilitada) y, como red, el servidor (422 fuera del GAM), que además re-deriva el precio.
    if (method === 'ps_delivery') return psDeliveryPriceFor(this.provinceValue());

    if (method === 'other_shop') {
      const shopName = this.shopNameValue();
      if (!shopName) return 0;
      const shop = cart?.partner_shops?.find((s) => s.name === shopName);
      return shop ? Number(shop.price) : 0;
    }

    // 'home' usa el Delivery id=1 que el backend embebe en el cart como
    // `ps_delivery`. Si free_delivery está activo y el subtotal alcanza el
    // umbral, el envío sale gratis.
    const psd = cart?.ps_delivery;
    if (!psd) return 0;
    if (psd.free_delivery && cart && cart.subtotal >= psd.min_for_free) return 0;
    return Number(psd.price);
  });

  /**
   * Abono inicial (30%) para la opción de apartado. Se calcula sobre el
   * total que ve el usuario en el sidebar (`cart.total + delivery`) para
   * que el monto mostrado coincida con el "Total" del resumen.
   */
  protected readonly layawayDeposit = computed<number>(() => {
    const total = (this.cart()?.total ?? 0) + this.computedDeliveryPrice();
    return Math.round(total * LAYAWAY_DEPOSIT_PERCENT);
  });

  /** Banner descriptivo bajo el selector de método. Texto por método. */
  protected readonly deliveryMethodHint = computed<string | null>(() => {
    const m = this.deliveryMethodValue();
    switch (m) {
      case 'ps':
        return 'Has seleccionado "Retirar en tienda". Te contactaremos en cuanto tu pedido esté listo para recoger.';
      case 'uber':
        return 'Tú envías un mensajero (Uber Flash, etc.) cuando te confirmemos que el pedido está listo. El costo del mensajero corre por tu cuenta.';
      case 'other_shop':
        return 'Tu pedido se enviará a la tienda partner que selecciones. Solo aplica para singles.';
      case 'home':
        return 'Recibe tu pedido por Correos de Costa Rica en la dirección que indiques.';
      case 'ps_delivery':
        return PS_DELIVERY_COPY.coverageNote;
      default:
        return null;
    }
  });

  /** True si el par provincia+cantón actual está dentro de la cobertura del mensajero propio. */
  protected readonly psDeliveryCovered = computed(() =>
    isPsDeliveryCovered(this.provinceValue(), this.cantonValue()),
  );

  /**
   * Deshabilita la opción «Mensajero Perrón Store» en el ORDEN INVERSO: el cliente está en «home»
   * (único método —además de ps_delivery— que muestra la dirección) con un cantón fuera de cobertura.
   * Se acota a `home` a propósito: en ps/uber/other_shop no hay dirección activa (y los `toSignal` de
   * cantón pueden quedar obsoletos tras un reset silencioso), así que la opción sigue disponible; si ya
   * es ps_delivery, el dialog reconduce (evita el estado contradictorio seleccionado+deshabilitado).
   */
  protected readonly psDeliveryOptionDisabled = computed(
    () =>
      this.deliveryMethodValue() === 'home' && !!this.cantonValue() && !this.psDeliveryCovered(),
  );

  /** Dialog «Cambiamos tu método de envío» (cantón no cubierto con ps_delivery seleccionado). */
  protected readonly coverageDialogOpen = signal(false);
  /** Modal informativo «ver destinos disponibles» (provincias, tarifas y cantones). */
  protected readonly destinationsOpen = signal(false);
  protected readonly psDeliveryCopy = PS_DELIVERY_COPY;
  /** Tarifa del mensajero para la provincia elegida (o el mínimo, mostrado como «Desde»). */
  protected readonly psDeliveryPrice = computed(() => psDeliveryPriceFor(this.provinceValue()));
  /** True si la provincia elegida tiene tarifa propia → mostrar precio EXACTO en vez de «Desde». */
  protected readonly psDeliveryPriceExact = computed(() =>
    isPsDeliveryProvince(this.provinceValue()),
  );

  /** Cantones de la provincia actualmente seleccionada. Reactivo al form y al JSON. */
  protected readonly availableCantons = computed(() => {
    // Dependencias explícitas para que el computed reaccione al cargar el JSON.
    this.provinces();
    return this.locationsService.cantonsOf(this.provinceValue());
  });

  protected readonly availableDistricts = computed(() => {
    this.provinces();
    return this.locationsService.districtsOf(this.provinceValue(), this.cantonValue());
  });

  constructor() {
    // ── Autofill silencioso de Contacto cuando hay sesión activa ───────
    // Política: NO sobreescribimos campos que el user ya editó manualmente
    // (control dirty). Esto evita destruir trabajo si el user empieza a
    // teclear y luego decide loguearse.
    //
    // Usamos `toObservable + take(1)` en vez de `effect()` porque el effect
    // sólo re-corría fiablemente cuando `currentUser` cambiaba de valor.
    // Si el componente montaba mientras el signal aún era null (ruta lazy
    // cargando antes de que `auth.me()` complete en APP_INITIALIZER), el
    // patch se perdía. La suscripción one-shot garantiza disparar cuando
    // llegue el primer usuario, aunque sea después del mount.
    toObservable(this.currentUser)
      .pipe(
        filter((u): u is User => !!u),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((user) => {
        const client = user.client;
        const contact = this.checkoutForm.controls.contact;
        const patch: ContactPatch = {};

        if (!contact.controls.email.dirty && user.email) {
          patch.email = user.email;
        }
        if (!contact.controls.name.dirty) {
          const name = client?.name ?? user.name ?? '';
          if (name) patch.name = name;
        }
        if (!contact.controls.surnames.dirty && client?.surnames) {
          patch.surnames = client.surnames;
        }
        if (!contact.controls.phone.dirty && client?.phone) {
          patch.phone = client.phone;
        }

        if (Object.keys(patch).length > 0) {
          contact.patchValue(patch, { emitEvent: false });
        }
      });

    // ── Autofill de DIRECCIÓN cuando hay sesión ────────────────────────
    // Complementa el autofill de contacto de arriba trayendo la dirección
    // guardada del cliente (el backend la actualiza en cada compra) por la
    // vía unificada `applyRememberedAddress()`. Mismo patrón que el contacto
    // (toObservable + take(1)) porque `currentUser` puede llegar tras el mount;
    // y SSR-safe por el mismo motivo: en server el signal es null, no patchea y
    // no hay hydration mismatch. El orden respecto al restore de método no es
    // crítico: si el método aterriza en recogida y la dirección se resetea, la
    // re-precarga del `valueChanges` la repone al volver a un método a domicilio.
    toObservable(this.currentUser)
      .pipe(
        filter((u): u is User => !!u),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.applyRememberedAddress());

    // ── Restaurar el último método de envío usado ──────────────────────
    // Preselecciona el método con el que el cliente completó su último
    // pedido (persistido en localStorage por la página de éxito). Aplica a
    // todos los clientes. En afterNextRender para evitar hydration mismatch
    // (server pinta 'ps'; el cambio ocurre tras hydration). Va ANTES del
    // autofill de invitado para que, si se restaura 'home', el relleno de la
    // dirección posterior no se pierda (el cambio de método resetea la
    // dirección solo cuando el método no la requiere).
    afterNextRender(() => {
      const saved = this.deliveryPref.getLastMethod();
      const control = this.deliveryGroup.controls.method;
      if (!saved || control.dirty || saved === control.value) return;
      // Método desactivado: nos quedamos en el defecto 'ps'. El caso
      // 'other_shop' con cart no elegible lo corrige el effect de más abajo.
      if (saved === 'ps_delivery' && !this.psDeliveryEnabled) return;
      control.setValue(saved as CheckoutDeliveryMethod);
    });

    // ── Autofill desde GuestProfileService cuando NO hay sesión ──────
    // Solo en cliente (SSR no tiene localStorage). Lo hacemos en
    // afterNextRender para evitar hydration mismatch: el form arranca
    // vacío en server y se rellena tras hydration.
    afterNextRender(() => {
      if (this.isAuthenticated()) return;
      const profile = this.guestProfileService.getProfile();
      if (!profile) return;

      const contact = this.checkoutForm.controls.contact;
      const contactPatch: ContactPatch = {};

      if (!contact.controls.name.dirty && profile.name) contactPatch.name = profile.name;
      if (!contact.controls.surnames.dirty && profile.surnames)
        contactPatch.surnames = profile.surnames;
      if (!contact.controls.email.dirty && profile.email) contactPatch.email = profile.email;
      if (!contact.controls.phone.dirty && profile.phone) contactPatch.phone = profile.phone;

      if (Object.keys(contactPatch).length > 0) {
        contact.patchValue(contactPatch, { emitEvent: false });
      }

      // La dirección se rellena por la vía unificada: sin sesión,
      // `applyRememberedAddress()` lee del GuestProfileService.
      this.applyRememberedAddress();
    });

    // ── Catálogo de ubicaciones ────────────────────────────────────────
    // No-op en SSR. Se lanza en cliente en cuanto monta el componente.
    this.locationsService.ensureLoaded();

    // ── Validators condicionales según método de entrega ───────────────
    // home / ps_delivery → address REQUIRED (salvo hood, siempre opcional).
    // other_shop → shop_name REQUIRED.
    // ps / uber → ningún campo extra requerido.
    // Además limpiamos los valores de los grupos que dejan de aplicar para
    // no enviar datos basura en el payload de 6e.
    this.deliveryGroup.controls.method.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((method) => {
        const requiresAddress = method === 'home' || method === 'ps_delivery';
        const requiresShop = method === 'other_shop';

        setRequiredOnControls(
          this.addressGroup,
          ['province', 'canton', 'district', 'directions'],
          requiresAddress,
        );
        setRequiredOnControls(this.addressGroup, ['hood'], false);

        const shopControl = this.deliveryGroup.controls.shop_name;
        shopControl.setValidators(requiresShop ? [Validators.required] : null);
        shopControl.updateValueAndValidity({ emitEvent: false });

        if (!requiresAddress) {
          this.addressGroup.reset(
            { province: '', canton: '', district: '', hood: '', directions: '' },
            { emitEvent: false },
          );
          this.syncAddressSignals();
        } else {
          // Al pasar a un método a domicilio reponemos la dirección recordada
          // (cuenta si hay sesión, localStorage si es invitado) en los campos
          // que el usuario no haya tocado. Idempotente y respeta `dirty`.
          this.applyRememberedAddress();
          this.reconcilePsDeliveryCoverage();
        }
        if (!requiresShop) {
          shopControl.setValue('', { emitEvent: false });
        }

        // Las 4 opciones de pago son fijas y NO dependen del método de envío,
        // así que NO reseteamos `payment_method` al cambiar el envío: la
        // selección del cliente se mantiene estable.
      });

    // ── Fallback other_shop → ps cuando el cart deja de ser elegible ───
    // Caso edge: user seleccionó 'other_shop' y luego añadió un no-single
    // en otra pestaña. El cart pasa a intershopBlocked y hay que forzar
    // el cambio para que no quede en un estado sin opción de progresar.
    // Sin { emitEvent: false } → queremos que dispare el subscribe de
    // method y recalcule validators/resets normalmente.
    effect(() => {
      if (this.intershopBlocked() && this.deliveryMethodValue() === 'other_shop') {
        this.deliveryGroup.controls.method.setValue('ps');
      }
    });

    // ── Umbral mínimo de apartado (D2/D3, docs/specs/checkout-apartados-umbral-plan.md) ──
    // D2: deshabilita el CONTROL (no solo el atributo visual) vía Reactive Forms
    // (disable()/enable()), para que Angular bloquee la interacción real a través del
    // ControlValueAccessor — un [attr.disabled] manual no es fiable junto a formControlName.
    effect(() => {
      const control = this.paymentGroup.controls.is_layaway;
      if (this.layawayEligible()) {
        if (control.disabled) control.enable({ emitEvent: false });
      } else if (control.enabled) {
        control.disable({ emitEvent: false });
      }
    });

    // D3: si la mercancía cae bajo el umbral con el toggle YA activo, se desmarca solo. El
    // setValue(false) dispara la suscripción de is_layaway más abajo, que YA resetea
    // layawayConsented/layawayNoticeOpen — no hace falta limpiarlos aquí.
    effect(() => {
      if (this.isLayaway() && !this.layawayEligible()) {
        this.paymentGroup.controls.is_layaway.setValue(false);
      }
    });

    // ── Cascade reset: provincia → cantón + distrito ───────────────────
    this.addressGroup.controls.province.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((province) => {
        this.provinceValue.set(province ?? '');
        this.addressGroup.patchValue({ canton: '', district: '' }, { emitEvent: false });
        this.cantonValue.set('');
      });

    this.addressGroup.controls.canton.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((canton) => {
        this.cantonValue.set(canton ?? '');
        this.addressGroup.patchValue({ district: '' }, { emitEvent: false });
        this.reconcilePsDeliveryCoverage();
      });

    // ── Consentimiento al ACTIVAR la modalidad «Apartar con abonos» ───────
    // El apartado es un toggle aparte de los métodos. Al activarlo mostramos el
    // consentimiento bloqueante (solo la primera vez); al aceptarlo se revela el
    // selector del método del abono (sinpe/transfer). La tarjeta del 30% está
    // gated (Pasarela 5): si había 'online' elegido, lo limpiamos para no quedar
    // en estado inválido.
    this.paymentGroup.controls.is_layaway.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((on) => {
        if (on) {
          // Bajo `stub`, la tarjeta no existe en el selector del abono. Si venía elegida como método
          // directo, limpiarla evita conservar un `online` oculto que el backend rechazaría para
          // apartado. Con ONVO sí se conserva porque la tarjeta del 30% está habilitada.
          if (!this.cardEnabled && this.paymentGroup.controls.payment_method.value === 'online') {
            this.paymentGroup.controls.payment_method.setValue(null);
          }
          // Una sola vez: si ya consintió antes, saltamos el modal y revelamos
          // directamente el selector del 30%.
          if (this.notice.hasSeen(LAYAWAY_NOTICE_KEY)) {
            this.layawayConsented.set(true);
          } else {
            this.layawayNoticeOpen.set(true);
          }
        } else {
          this.layawayConsented.set(false);
          this.layawayNoticeOpen.set(false);
        }
      });

    // ── Aviso del comprobante al ELEGIR SINPE/transferencia ───────────────
    // Un único enganche cubre los dos radiogroups (método directo y selector del
    // abono), porque ambos escriben `payment_method`. Bloqueante y de una sola
    // vez: solo aparece la primera vez que el cliente elige uno de esos métodos.
    // Los `setValue(null)` programáticos (p. ej. al activar apartado) no lo abren.
    this.paymentGroup.controls.payment_method.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((m) => {
        if ((m === 'sinpe' || m === 'transfer') && !this.notice.hasSeen(PROOF_NOTICE_KEY)) {
          this.proofNoticeOpen.set(true);
        }
      });

    // ── Aviso NO bloqueante al elegir Uber Flash como método de envío ────
    // Una sola vez por sesión de checkout (uberNoticeShown en memoria, no
    // localStorage): no reaparece en cada re-render, pero sí en una visita
    // nueva. Nunca bloquea continuar (D2 de la feature).
    this.deliveryGroup.controls.method.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((method) => {
        if (method === 'uber' && !this.uberNoticeShown) {
          this.uberNoticeShown = true;
          this.uberNoticeOpen.set(true);
        }
      });
  }

  protected get contactGroup() {
    return this.checkoutForm.controls.contact;
  }

  protected get deliveryGroup() {
    return this.checkoutForm.controls.delivery;
  }

  protected get addressGroup() {
    return this.checkoutForm.controls.delivery.controls.address;
  }

  protected get paymentGroup() {
    return this.checkoutForm.controls.payment;
  }

  /**
   * Dirección «recordada» del cliente según la fuente disponible:
   *  - con sesión: los campos guardados en `client` (el backend los actualiza
   *    en cada compra). `directions` cae a `client.address` (campo legacy y
   *    solo-lectura) para clientes antiguos con la dirección monolítica.
   *  - sin sesión: el último perfil de invitado (localStorage, SSR-safe).
   * Devuelve null si no hay fuente (usuario sin `client`, o invitado sin
   * compras previas).
   */
  private rememberedAddress(): AddressPatch | null {
    if (this.isAuthenticated()) {
      const client = this.currentUser()?.client;
      if (!client) return null;
      return {
        province: client.province ?? undefined,
        canton: client.canton ?? undefined,
        district: client.district ?? undefined,
        hood: client.hood ?? undefined,
        directions: client.directions ?? client.address ?? undefined,
      };
    }
    const profile = this.guestProfileService.getProfile();
    if (!profile) return null;
    return {
      province: profile.province,
      canton: profile.canton,
      district: profile.district,
      hood: profile.hood,
      directions: profile.directions,
    };
  }

  /**
   * Rellena el bloque de dirección desde `rememberedAddress()` sin pisar los
   * campos que el usuario ya editó (`dirty`). Usa `{ emitEvent: false }` para
   * no disparar el cascade provincia→cantón→distrito, de modo que los tres
   * valores coexisten. `patchValue` no marca `dirty`, así que es idempotente y
   * puede reaplicarse (p. ej. al volver a un método a domicilio).
   */
  private applyRememberedAddress(): void {
    const remembered = this.rememberedAddress();
    if (!remembered) return;

    const address = this.addressGroup;
    const patch: AddressPatch = {};
    if (!address.controls.province.dirty && remembered.province)
      patch.province = remembered.province;
    if (!address.controls.canton.dirty && remembered.canton) patch.canton = remembered.canton;
    if (!address.controls.district.dirty && remembered.district)
      patch.district = remembered.district;
    if (!address.controls.hood.dirty && remembered.hood) patch.hood = remembered.hood;
    if (!address.controls.directions.dirty && remembered.directions)
      patch.directions = remembered.directions;

    if (Object.keys(patch).length > 0) {
      address.patchValue(patch, { emitEvent: false });
      this.syncAddressSignals();
      this.reconcilePsDeliveryCoverage();
    }
  }

  /** Sincroniza los signals de ubicación tras un patch/reset que no emite `valueChanges`. */
  private syncAddressSignals(): void {
    this.provinceValue.set(this.addressGroup.controls.province.value ?? '');
    this.cantonValue.set(this.addressGroup.controls.canton.value ?? '');
  }

  /** Abre el aviso cuando el método activo y la dirección completa quedan fuera de cobertura. */
  private reconcilePsDeliveryCoverage(): void {
    if (this.deliveryMethodValue() !== 'ps_delivery') return;
    const province = this.addressGroup.controls.province.value;
    const canton = this.addressGroup.controls.canton.value;
    if (province && canton && !isPsDeliveryCovered(province, canton)) {
      this.coverageDialogOpen.set(true);
    }
  }

  protected errorFor(path: string, label?: string): string {
    return errorMessageFor(this.checkoutForm.get(path), label);
  }

  protected onOpenLogin(): void {
    this.authModalService.open('login');
  }

  /** El usuario confirmó la lectura del aviso de apartados: revela el selector del 30%. */
  protected onLayawayAcknowledge(): void {
    this.layawayNoticeOpen.set(false);
    this.layawayConsented.set(true);
    this.notice.markSeen(LAYAWAY_NOTICE_KEY);
  }

  /** El usuario confirmó la lectura del aviso del comprobante: no vuelve a mostrarse. */
  protected onProofNoticeAcknowledge(): void {
    this.proofNoticeOpen.set(false);
    this.notice.markSeen(PROOF_NOTICE_KEY);
  }

  protected onUberNoticeAcknowledge(): void {
    this.uberNoticeOpen.set(false);
  }

  /**
   * El cliente aceptó el aviso de cobertura: cambiamos el método a Correos (home). El `setValue`
   * dispara el recálculo de envío y totales vía `computedDeliveryPrice` y reaplica los validadores
   * de dirección (misma dirección sirve para Correos). El cantón queda como estaba.
   */
  protected onCoverageDialogAcknowledge(): void {
    this.coverageDialogOpen.set(false);
    this.deliveryGroup.controls.method.setValue('home');
    this.showCoverageSwitchedToast();
  }

  /**
   * Snackbar de refuerzo tras cambiar a Correos por cantón no cubierto (el cliente veía el dialog
   * como demasiado sutil). Se auto-oculta a los 10 s; «Entendido» solo lo cierra antes y «Ver destinos»
   * abre el modal de cobertura.
   */
  private showCoverageSwitchedToast(): void {
    this.toastService.show({
      type: 'info',
      message: PS_DELIVERY_COPY.switchedNote,
      duration: 10000,
      actions: [
        { label: PS_DELIVERY_COPY.destinationsLink, run: () => this.destinationsOpen.set(true) },
        { label: PS_DELIVERY_COPY.switchedAck },
      ],
    });
  }

  /**
   * «Finalizar» del modal de SINPE/transferencia: crea el preorder con el
   * comprobante adjunto (create-on-finalize). Solo si todo va bien se vacía el
   * carrito (lo hace el servicio) y se navega a éxito. Si el backend rechaza
   * (p. ej. stock agotado mientras el modal estaba abierto), mostramos el error
   * en el modal y NO navegamos ni vaciamos el carrito.
   */
  protected onPaymentFinalize(file: File): void {
    const cart = this.cart();
    const context = this.pendingContext;
    if (!cart || !context || this.paymentModalSubmitting()) return;

    const payload = this.buildPayload();
    // Propagamos el token del HOLD (si la puerta lo abrió) para que el backend consuma la reserva con
    // gracia bajo lock en vez del pre-check raw. Sin token → fallback raw (backward-compat).
    const token = this.reservationToken();
    const requestPayload: PreorderRequest = token ? { ...payload, session_token: token } : payload;
    this.paymentModalSubmitting.set(true);
    this.paymentModalError.set(null);

    this.cartService
      .cartToPreorder(requestPayload, cart.id, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (preorder) => {
          if (!this.isAuthenticated()) this.persistGuestProfile(payload);
          this.paymentModalSubmitting.set(false);
          this.paymentModalOpen.set(false);
          this.pendingContext = null;
          this.reservationToken.set(null);
          this.reservationExpiresAt.set(null);
          this.router.navigate(['/checkout/exito', preorder.token], {
            state: { preorder, preorderHasSingles: context.hasSingles },
            replaceUrl: false,
          });
        },
        error: (err) => {
          this.paymentModalSubmitting.set(false);
          this.paymentModalError.set(this.finalizeErrorMessage(err));
          // Stock/promo (raw o puerta): refetch del cart para que el sidebar refleje el estado real.
          if (
            err instanceof PreorderError &&
            ['stock', 'promo', 'stock_held', 'stock_out', 'stock_insufficient'].includes(err.reason)
          ) {
            this.cartService.getCart().subscribe();
          }
        },
      });
  }

  /** Mensaje del error de finalización: el hold ocupado usa el rótulo verbatim; el resto, su mensaje. */
  private finalizeErrorMessage(err: unknown): string {
    // Esta rama consume un HOLD, y con `session_token` válido el gate NO se
    // re-evalúa (R12): un 403 aquí no debería existir. Se mapea igualmente
    // para que, si el servidor cambiara de criterio, el cliente lea el motivo
    // real en vez del genérico.
    if (err instanceof EarlyAccessGateRejection) return err.gate.message;
    if (err instanceof PreorderError) {
      if (err.reason === 'stock_held') return CHECKOUT_COPY.reservation.stockHeld;
      if (err.reason === 'stock_out') return CHECKOUT_COPY.reservation.stockOut;
      return err.message;
    }
    return 'No pudimos procesar tu pedido.';
  }

  /** «Cancelar» / Escape del modal: cierra sin crear nada; el carrito y el hold quedan (el hold caduca solo). */
  protected onPaymentDismiss(): void {
    if (this.paymentModalSubmitting()) return;
    this.paymentModalOpen.set(false);
    this.paymentModalError.set(null);
    this.pendingContext = null;
    this.reservationToken.set(null);
    this.reservationExpiresAt.set(null);
  }

  /**
   * El HOLD expiró con el modal abierto: lo cerramos, limpiamos la reserva y avisamos. El cliente
   * puede volver a «Confirmar pedido» (se reservará de nuevo si quedan unidades libres). Espejo de
   * prepurchase-modal.onReservationExpired.
   */
  protected onReservationExpired(): void {
    if (!this.paymentModalOpen()) return;
    this.paymentModalOpen.set(false);
    this.paymentModalError.set(null);
    this.pendingContext = null;
    this.reservationToken.set(null);
    this.reservationExpiresAt.set(null);
    this.toastService.show({ type: 'error', message: CHECKOUT_COPY.reservation.expired });
  }

  /**
   * Error de la PUERTA de reserva (SINPE/transferencia, antes de abrir el modal): enruta por el `error`
   * del 409 uniforme (stock_out → modal «avísame»; stock_insufficient → aviso + refetch; stock_held →
   * rótulo verbatim) o por el 429 (throttle). NO abre el modal de pago.
   */
  private handleReserveError(err: unknown): void {
    this.submitting.set(false);

    if (this.handleEarlyAccessGate(err)) return;

    if (err instanceof HttpErrorResponse) {
      if (err.status === 429) {
        this.toastService.show({ type: 'error', message: CHECKOUT_COPY.reservation.throttled });
        return;
      }
      if (err.status === 409) {
        this.routeStockConflict(
          (err.error?.error ?? '').toString(),
          (err.error?.message ?? '').toString(),
          err.error?.items,
        );
        return;
      }
    }

    this.toastService.show({
      type: 'error',
      message: 'No pudimos reservar tu pedido. Inténtalo de nuevo.',
    });
  }

  /**
   * 403 del gate de Acceso VIP en `checkout/{cart}/reserve`,
   * `checkout/{cart}/payment-intent` o la rama legacy de `cart-to-preorder`.
   * Devuelve `true` si lo ha manejado (y el caller no debe seguir enrutando).
   *
   * Sin esto, estos 403 caerían en el genérico «No pudimos procesar tu pedido»
   * y el cliente no sabría qué línea quitar. Abrimos el drawer para que vea la
   * línea bloqueada, que ya se pinta sola: el 403 nos da el umbral real, con el
   * que `blockedLines` deja de depender del fallback.
   */
  private handleEarlyAccessGate(err: unknown): boolean {
    const gate = err instanceof EarlyAccessGateRejection ? err.gate : mapEarlyAccessGate403(err);
    if (!gate) return false;

    if (gate.threshold !== null) this.pointsService.recordThreshold(gate.threshold);
    this.toastService.show({ type: 'error', message: gate.message });
    this.cartDrawerService.open();
    return true;
  }

  /**
   * Enruta un 409 de la puerta (compartido por la puerta manual y la de tarjeta). Si el 409 trae el
   * desglose por producto (`items[]`), reabre el cart drawer con las líneas en conflicto resaltadas y
   * sus unidades libres, para que el usuario ajuste cantidades o elimine líneas. Sin desglose (contrato
   * antiguo) mantiene el enrutado por código: stock_out → modal «avísame»; insufficient → aviso; held →
   * rótulo verbatim.
   */
  private routeStockConflict(code: string, backendMessage: string, items?: StockConflictItem[]): void {
    if (Array.isArray(items) && items.length > 0) {
      // El carrito local ya refleja lo que se envió a la puerta (la reserva no lo modifica), así que
      // abrimos el drawer con ese estado. NO refrescamos con getCart(): un fallo transitorio del refetch
      // vaciaría el drawer y descartaría el token del carrito justo a mitad del pago. Los ajustes
      // posteriores (updateLine/removeLine) ya actualizan el signal del carrito por sí mismos.
      this.cartDrawerService.openWithConflicts(items);
      this.toastService.show({ type: 'error', message: CHECKOUT_COPY.reservation.conflictHint });
      return;
    }

    if (code === 'stock_out') {
      this.notifyStockOut();
      return;
    }
    if (code === 'stock_insufficient') {
      this.toastService.show({
        type: 'error',
        message: backendMessage || 'Ajusta la cantidad para continuar.',
      });
      this.cartService.getCart().subscribe();
      return;
    }
    // stock_held (u otro 409 de la puerta): rótulo verbatim.
    this.toastService.show({ type: 'error', message: CHECKOUT_COPY.reservation.stockHeld });
  }

  /**
   * Agotado real (stock_out): ofrece el modal «avísame» del catálogo (donde vive la promesa REAL del
   * aviso, POST /create-alert) cuando el producto es inequívoco (carrito de una sola línea). En un
   * carrito multi-producto el producto es ambiguo → aviso SIN promesa. El StockAlertModalService se
   * resuelve perezosamente para no acoplar el checkout a su cadena HTTP en construcción.
   */
  private notifyStockOut(): void {
    const cart = this.cart();
    const singleLine = cart?.lines?.length === 1 ? cart.lines[0] : null;
    const product = singleLine?.product;

    if (product) {
      this.injector.get(StockAlertModalService).open(product as unknown as Product);
      return;
    }

    this.toastService.show({ type: 'error', message: CHECKOUT_COPY.reservation.stockOut });
  }

  protected onSubmit(): void {
    if (this.submitting()) return;

    // Marca todo el form como touched para mostrar errores en campos
    // pristine que el user no haya tocado.
    this.checkoutForm.markAllAsTouched();

    if (this.checkoutForm.invalid) {
      this.toastService.show({
        type: 'error',
        message: 'Revisa los campos marcados antes de continuar.',
      });
      this.focusFirstInvalidControl();
      return;
    }

    const cart = this.cart();
    if (!cart) {
      this.toastService.show({
        type: 'error',
        message: 'Tu carrito está vacío. Vuelve a la tienda para añadir productos.',
      });
      return;
    }

    const payment = this.checkoutForm.controls.payment.getRawValue();
    const method = payment.payment_method as PaymentMethod;
    const isLayaway = payment.is_layaway;
    const preorderHasSingles = this.hasSingles();
    // Total que ve el cliente (cart + envío). El cart sigue vivo (no se vacía
    // hasta el éxito), pero lo capturamos aquí para el importe del modal.
    const orderTotal = (cart.total ?? 0) + this.computedDeliveryPrice();

    // SINPE / transferencia (pago normal o abono inicial del apartado): create-on-finalize.
    // PUERTA de stock (D1): reservamos el HOLD ANTES de abrir el modal. Sin cupo → NO se abre el modal
    // (enrutamos el copy por `error`); con cupo → abrimos el modal de comprobante con el countdown del
    // hold. El preorder se crea al «Finalizar» (onPaymentFinalize) propagando el session_token.
    if (method === 'sinpe' || method === 'transfer') {
      const payload = this.buildPayload();
      this.submitting.set(true);
      this.paymentService
        .reserveCheckout(cart.id, payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (reservation) => {
            this.submitting.set(false);
            const deposit = this.layawayDeposit();
            this.reservationToken.set(reservation.sessionToken);
            this.reservationExpiresAt.set(reservation.reservationExpiresAt);
            this.pendingContext = { hasSingles: preorderHasSingles };
            this.paymentModalMethod.set(method);
            this.paymentModalIsLayaway.set(isLayaway);
            this.paymentModalAmount.set(isLayaway ? deposit : orderTotal);
            this.paymentModalRemaining.set(isLayaway ? orderTotal - deposit : 0);
            this.paymentModalError.set(null);
            this.paymentModalOpen.set(true);
          },
          error: (err) => this.handleReserveError(err),
        });
      return;
    }

    // 'online' (tarjeta). DOS caminos:
    //  - Pasarela ONVO activa → flujo DIFERIDO: NO creamos el preorder ni vaciamos el carrito
    //    todavía. Creamos el intent desde el carrito y navegamos al paso de pago; el pedido se
    //    materializa tras aprobarse el cobro. Si el pago falla, el carrito sigue intacto para reintentar.
    //  - Pasarela en 'stub' (prod aún no activa) → comportamiento previo: crea el preorder y va a
    //    éxito (enlace de pago diferido por correo). Release-safety (D2). Leemos `providerId` del
    //    PaymentService (no `environment`) para que los tests fuercen la rama vía override.
    const payload = this.buildPayload();
    this.submitting.set(true);

    if (this.paymentService.providerId === 'onvo') {
      this.paymentService
        .createCheckoutIntent(cart.id, payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (intent) => {
            if (!this.isAuthenticated()) this.persistGuestProfile(payload);
            this.router.navigate(['/checkout/pago/sesion', intent.sessionToken], {
              state: { checkoutIntent: intent, preorderHasSingles },
              replaceUrl: true,
            });
            // No reseteamos `submitting`: la página se desmonta.
          },
          error: (err) => this.handleOnlineError(err),
        });
      return;
    }

    this.cartService
      .cartToPreorder(payload, cart.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (preorder) => {
          if (!this.isAuthenticated()) this.persistGuestProfile(payload);
          this.router.navigate(['/checkout/exito', preorder.token], {
            state: { preorder, preorderHasSingles },
            replaceUrl: false,
          });
          // No reseteamos `submitting` porque la página se desmonta.
        },
        error: (err) => this.handleOnlineError(err),
      });
  }

  /**
   * Error del camino 'online' (intent diferido de ONVO o create-on-finalize del stub): muestra el
   * mensaje y, si fue stock/promo (422), refresca el carrito para que el sidebar refleje el estado real.
   */
  private handleOnlineError(err: unknown): void {
    this.submitting.set(false);

    if (this.handleEarlyAccessGate(err)) return;

    // 409/429 de la PUERTA de stock (createCheckoutIntent delega en reserveForCart): mismo enrutado
    // por `error` que la puerta manual (stock_out → «avísame», etc.).
    if (err instanceof HttpErrorResponse) {
      if (err.status === 429) {
        this.toastService.show({ type: 'error', message: CHECKOUT_COPY.reservation.throttled });
        return;
      }
      if (err.status === 409) {
        this.routeStockConflict(
          (err.error?.error ?? '').toString(),
          (err.error?.message ?? '').toString(),
          err.error?.items,
        );
        return;
      }
    }

    let message = 'No pudimos procesar tu pedido.';
    let stockOrPromo = false;

    if (err instanceof PreorderError) {
      message = err.message;
      stockOrPromo = err.reason === 'stock' || err.reason === 'promo';
    } else if (err instanceof HttpErrorResponse) {
      const backend = (err.error?.error ?? err.error?.message ?? '').toString();
      if (backend) message = backend;
      stockOrPromo = err.status === 422;
    }

    this.toastService.show({ type: 'error', message });
    if (stockOrPromo) this.cartService.getCart().subscribe();
  }

  /** Construye el payload exacto que espera el backend según método. */
  private buildPayload(): PreorderRequest {
    const contact = this.checkoutForm.controls.contact.getRawValue();
    const delivery = this.checkoutForm.controls.delivery.getRawValue();
    const address = this.addressGroup.getRawValue();
    const payment = this.checkoutForm.controls.payment.getRawValue();
    const cart = this.cart();
    const requiresAddress = this.requiresAddress();
    const requiresShop = this.requiresPartnerShop();

    const payload: PreorderRequest = {
      name: contact.name?.trim() ?? '',
      surnames: contact.surnames?.trim() ?? '',
      email: contact.email?.trim() ?? '',
      phone: contact.phone?.trim() || null,
      identification: null,
      province: requiresAddress ? address.province?.trim() || null : null,
      canton: requiresAddress ? address.canton?.trim() || null : null,
      district: requiresAddress ? address.district?.trim() || null : null,
      hood: requiresAddress ? address.hood?.trim() || null : null,
      directions: requiresAddress ? address.directions?.trim() || null : null,
      address: null,
      delivery_method: delivery.method as PreorderDeliveryMethod,
      delivery_price: this.computedDeliveryPrice(),
      shop_name: requiresShop ? delivery.shop_name?.trim() || null : null,
      payment_method: payment.payment_method as PreorderPaymentMethod,
      is_layaway: payment.is_layaway,
      promotional_code_id: cart?.promotional_code_id ?? null,
      discount: cart?.discounts ?? 0,
    };

    // online_payment se mantiene como conveniencia para código backend que lo
    // siga leyendo. Es true solo cuando el cliente eligió tarjeta ('online').
    payload.online_payment = payment.payment_method === 'online';

    return payload;
  }

  /**
   * Tras submit fallido, lleva foco + scroll al primer control inválido
   * dentro del form. Busca por la clase `ng-invalid` que Angular aplica
   * a inputs/selects/textareas (excluye FormGroups que también la reciben
   * por agregación). scrollIntoView con `block: 'center'` asegura
   * visibilidad incluso con teclado virtual en mobile. El setTimeout
   * evita que el browser cancele el smooth scroll al hacer focus inmediato.
   */
  private focusFirstInvalidControl(): void {
    const formEl = this.document.querySelector('.checkout__form');
    if (!formEl) return;

    const first = formEl.querySelector<HTMLElement>(
      'input.ng-invalid, select.ng-invalid, textarea.ng-invalid',
    );
    if (!first) return;

    first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => first.focus({ preventScroll: true }), 100);
  }

  /** Persiste los datos del invitado para autofill en próxima compra. */
  private persistGuestProfile(payload: PreorderRequest): void {
    const profile: GuestProfile = {
      name: payload.name || undefined,
      surnames: payload.surnames || undefined,
      email: payload.email || undefined,
      phone: payload.phone || undefined,
      province: payload.province || undefined,
      canton: payload.canton || undefined,
      district: payload.district || undefined,
      hood: payload.hood || undefined,
      directions: payload.directions || undefined,
    };
    this.guestProfileService.setProfile(profile);
  }
}
